import { EndSessionReason } from '#shared/types/analytics'

const DEFAULT_SEND_INTERVAL_MS = 90_000

type AnalyticsRoute = {
    path: string
    fullPath: string
    name?: string | symbol | null
}

let sendQueueIntervalMs = DEFAULT_SEND_INTERVAL_MS
let intervalId: ReturnType<typeof setInterval> | undefined
let initialized = false
let isSending = false
let navigationCount = 0
let navigationStartedAt: number | undefined
let sessionStartedAt = Date.now()
let pauseStartedAt = 0
let pausedDuration = 0
let isPaused = false
let hiddenSinceMs: number | undefined
let hiddenAccumulatedMs = 0
let navigationHiddenBaselineMs = 0

const generateUniqueId = (): string => {
    const date = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `${date}-${random}`
}

let userId = generateUniqueId()

type AnalyticsStart = {
    action: 'identify'
    timestamp: string
    context: { page: string }
    userId: string
}

type AnalyticsPage = {
    action: 'track'
    timestamp: string
    event: 'navigation'
    userId: string
    context: {
        route_name: string | null
    }
    properties: {
        path: string
        referrer: string | null
        referrer_external: boolean
        response_time_ms: number
        response_type: 'server' | 'client_navigation'
    }
}

type AnalyticsEnd = {
    action: 'end'
    timestamp: string
    context: {
        screen: string | null
        duration: number
        navigation_count: number
    }
    userId: string
    properties: { reason: EndSessionReason }
}

type AnalyticsQueueItem = AnalyticsStart | AnalyticsPage | AnalyticsEnd

let queue: AnalyticsQueueItem[] = []
let isDevMode = false
let router: ReturnType<typeof useRouter>

export enum SessionStatus {
    NONE,
    UNSTARTED,
    ACTIVE,
    DONE,
}

let sessionStatus = SessionStatus.NONE

const devLog = (...payload: unknown[]) => {
    if (isDevMode) console.log('Analytics:', ...payload)
}

const getPageName = (route: AnalyticsRoute): string => {
    // Keep analytics names stable between locales and exclude query/hash data.
    const withoutLocale = route.path.replace(/^\/(?:en|nl)(?=\/|$)/i, '')
    const withoutTrailingSlash = withoutLocale.replace(/\/+$/, '')
    return withoutTrailingSlash || '/'
}

const getUtmSource = (route: AnalyticsRoute): string | null => {
    const source = new URL(route.fullPath, window.location.origin).searchParams.get('utm_source')?.trim()
    return source || null
}

// document.referrer is only ever a real cross-site URL for the initial page
// load; client-side navigations pass an internal page name instead, which is
// never external.
const isExternalReferrer = (referrer: string | null): boolean => {
    if (!referrer) return false
    try {
        return new URL(referrer).origin !== window.location.origin
    } catch {
        return false
    }
}

const getInitialResponseTime = (): number => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (!navigation) return 0
    return Math.max(0, Math.round(navigation.responseEnd - navigation.requestStart))
}

const getSessionDuration = () => {
    const activePause = isPaused ? Date.now() - pauseStartedAt : 0
    return Math.max(0, Date.now() - sessionStartedAt - pausedDuration - activePause)
}

const createEndItem = (reason: EndSessionReason): AnalyticsEnd => ({
    action: 'end',
    timestamp: new Date().toISOString(),
    userId,
    context: {
        duration: getSessionDuration(),
        navigation_count: navigationCount,
        screen: getPageName(router.currentRoute.value),
    },
    properties: { reason },
})

const sendBeacon = (endSession: boolean) => {
    if (endSession && sessionStatus === SessionStatus.ACTIVE) {
        queue.push(createEndItem(EndSessionReason.WINDOW_CLOSED))
        sessionStatus = SessionStatus.DONE
    }
    if (!queue.length) return

    const payload = JSON.stringify({ batch: queue })
    if (navigator.sendBeacon('/api/analytics', payload)) queue = []
}

// Total time the tab has spent hidden, so navigation timing below can exclude
// backgrounded/throttled time instead of counting it as "response time".
const getTotalHiddenMs = (): number => {
    const openSpan = hiddenSinceMs === undefined ? 0 : performance.now() - hiddenSinceMs
    return hiddenAccumulatedMs + openSpan
}

const handleVisibilityChange = () => {
    if (document.hidden) {
        hiddenSinceMs = performance.now()
        if (sessionStatus === SessionStatus.ACTIVE) Analytics.pause_session()
        // This also covers mobile browsers, where beforeunload/pagehide may not run.
        sendBeacon(false)
    } else {
        if (hiddenSinceMs !== undefined) {
            hiddenAccumulatedMs += performance.now() - hiddenSinceMs
            hiddenSinceMs = undefined
        }
        if (sessionStatus === SessionStatus.ACTIVE) Analytics.resume_session()
    }
}

const trackPage = (
    route: AnalyticsRoute,
    responseTime: number,
    responseType: AnalyticsPage['properties']['response_type'],
    referrer: string | null,
    referrerExternal?: boolean,
) => {
    const pageName = getPageName(route)
    if (sessionStatus === SessionStatus.ACTIVE) navigationCount += 1
    queue.push({
        action: 'track',
        timestamp: new Date().toISOString(),
        event: 'navigation',
        userId,
        context: {
            route_name: route.name?.toString() ?? null,
        },
        properties: {
            path: pageName,
            referrer,
            referrer_external: referrerExternal ?? isExternalReferrer(referrer),
            response_time_ms: Math.max(0, Math.round(responseTime)),
            response_type: responseType,
        },
    })
}

export const Analytics = {
    get_status: () => sessionStatus,

    set_dev_mode: (state: boolean) => {
        isDevMode = state
    },

    set_interval: (interval: number) => {
        sendQueueIntervalMs = interval
        if (intervalId) {
            clearInterval(intervalId)
            intervalId = setInterval(() => void Analytics.send_queue(), sendQueueIntervalMs)
        }
        devLog(`Queue interval: ${Math.round(interval / 1000)} seconds`)
    },

    init: () => {
        if (initialized) return

        initialized = true
        router = useRouter()
        sessionStatus = SessionStatus.UNSTARTED
        intervalId = setInterval(() => void Analytics.send_queue(), sendQueueIntervalMs)

        router.beforeEach(() => {
            navigationStartedAt = performance.now()
            navigationHiddenBaselineMs = getTotalHiddenMs()
        })
        router.afterEach((to, from, failure) => {
            if (failure) return
            const responseTime = navigationStartedAt === undefined
                ? 0
                : (performance.now() - navigationStartedAt) - (getTotalHiddenMs() - navigationHiddenBaselineMs)
            navigationStartedAt = undefined
            trackPage(to, responseTime, 'client_navigation', getPageName(from))
        })

        window.addEventListener('pagehide', event => sendBeacon(!event.persisted))
        window.addEventListener('beforeunload', () => sendBeacon(true))
        document.addEventListener('visibilitychange', handleVisibilityChange)
        devLog('Enabled')
    },

    start_session: (route: AnalyticsRoute) => {
        if (sessionStatus === SessionStatus.ACTIVE) return

        userId = generateUniqueId()
        sessionStartedAt = Date.now()
        navigationCount = 0
        pausedDuration = 0
        pauseStartedAt = 0
        isPaused = false
        sessionStatus = SessionStatus.ACTIVE

        queue.push({
            action: 'identify',
            timestamp: new Date().toISOString(),
            userId,
            // Preserve the original identify-event value for existing reports.
            context: { page: route.name?.toString() ?? '' },
        })
        const utmSource = getUtmSource(route)
        trackPage(
            route,
            getInitialResponseTime(),
            'server',
            (utmSource ?? document.referrer) || null,
            utmSource ? true : undefined,
        )
        devLog('Started session')
    },

    pause_session: () => {
        if (isPaused) return
        isPaused = true
        pauseStartedAt = Date.now()
    },

    resume_session: () => {
        if (!isPaused) return
        pausedDuration += Date.now() - pauseStartedAt
        pauseStartedAt = 0
        isPaused = false
    },

    stop_session: async (reason: EndSessionReason) => {
        if (sessionStatus !== SessionStatus.ACTIVE) return
        sessionStatus = SessionStatus.DONE
        queue.push(createEndItem(reason))
        await Analytics.send_queue()
    },

    send_queue: async () => {
        if (!queue.length || isSending) return

        const batch = queue.splice(0, queue.length)
        isSending = true
        try {
            await $fetch('/api/analytics', {
                method: 'POST',
                body: { batch },
                keepalive: true,
            })
            devLog(`Sent ${batch.length} event(s)`)
        } catch (error) {
            queue.unshift(...batch)
            console.error('Could not send analytics batch', error)
        } finally {
            isSending = false
        }
    },
}
